<template>
  <v-container
    fluid
    text-center
  >
    <v-row>
      <v-col
        cols="12"
        class="dropdown-card"
      >
        <v-card id="filter-by-dropdown">
          <v-row>
            <v-col cols="4">
              <FilterByDropdown
                ref="programFilter"
                filter-type="program"
              />
            </v-col>
            <v-col cols="4">
              <FilterByDropdown
                ref="projectFilter"
                filter-type="project"
              />
            </v-col>
            <v-col cols="4">
              <FilterByDropdown
                ref="surveyFilter"
                filter-type="survey"
              />
            </v-col>
            <v-row
              class="filters"
              justify="center"
            >
              <v-btn
                outlined
                small
                @click="reset"
              >
                Clear Filters
              </v-btn>
            </v-row>
          </v-row>
          <v-divider /><br>
          <h2
            v-if="currentObject.model === 'survey'"
          >
            Total respondents:
            {{ totalRespondents === 0 ? totalRespondents : animatedNumber }}
          </h2>

          <br>
          <!-- <v-btn
            v-if="surveyNum !== -1"
            color="primary"
            top
            class="export-button"
            @click.native="print()"
          >
            Export Charts
          </v-btn> -->
          <v-progress-circular
            v-if="showLoading"
            indeterminate
            :size="50"
            color="primary"
          />
        </v-card>
      </v-col>
    </v-row>

    <!-- CHARTS -->
    <v-row
      v-if="showCharts"
      id="charts-area"
      ref="printMe"
      wrap
    >
      <v-col :cols="size">
        <ResponseOverTime />
      </v-col>

      <template v-for="question in surveyObject">
        <v-col
          v-if="isPieChart(question)"
          :key="question.pk"
          :cols="size"
        >
          <ChartKick
            :question-name="question.name"
            :question-title="question.title.default"
            :multiple-options="false"
            chart-type="pie"
          />
        </v-col>

        <v-col
          v-else-if="isOneBarChart(question)"
          :key="question.pk"
          :cols="size"
        >
          <ChartKick
            :question-name="question.name"
            :question-title="question.title.default"
            :multiple-options="false"
            chart-type="onebar"
          />
        </v-col>

        <v-col
          v-else-if="isBarChart(question)"
          :key="question.pk"
          :cols="size"
        >
          <ChartKick
            :question-name="question.name"
            :question-title="question.title.default"
            :multiple-options="true"
            chart-type="bar"
          />
        </v-col>

        <v-col
          v-else-if="isWordCloud(question)"
          :key="question.pk"
          :cols="size"
        >
          <WordCloud
            :question-name="question.name"
            :question-title="question.title.default"
          />
        </v-col>
      </template>
      <!-- Add any special chart or specific ones here -->
    </v-row>

    <Aggregate
      v-else-if="showAggregateData"
      :key="aggregateDatakey"
      :object-data="currentObject"
      style="max-width: 100%;"
    />
  </v-container>
</template>

<script>
import FilterByDropdown from "@/components/Dashboard/Charts/FilterByDropdown";
import ResponseOverTime from "@/components/Dashboard/Charts/ResponseOverTime";
import ChartKick from "@/components/Dashboard/Charts/ChartKick";
import WordCloud from "@/components/Dashboard/Charts/WordCloud";
import Aggregate from "@/components/Dashboard/Charts/Aggregate";
import { mapState, mapActions } from "vuex";

import jsPDF from "jspdf";
import image2base64 from "image-to-base64";

export default {
  name: "Charts",
  components: {
    FilterByDropdown,
    ResponseOverTime,
    ChartKick,
    WordCloud,
    Aggregate
  },
  props: {},
  data() {
    return {
      output: null,
      chartData: null,
      tweenedNumber: 0,
      showLoading: false
    };
  },
  computed: {
    ...mapState({
      selectedProgram: state => state.programs.selectedProgram || "",
      selectedProject: state => state.projects.selectedProject || "",
      selectedSurvey: state => state.survey.selectedSurvey || "",
      selectedSurveyNumber: state => state.survey.selectedSurveyNumber,
      surveys: state => state.survey.surveys,
      surveyQuestions: state => state.survey.selectedSurveyQuestions,
      responses: state => state.charts.charts
    }),
    surveyNum() {
      return this.selectedSurveyNumber === null ? 0 : this.selectedSurveyNumber;
    },
    totalRespondents() {
      return this.responses.filter(survey => {
        return survey.survey === this.selectedSurveyNumber;
      }).length;
    },
    animatedNumber() {
      return this.tweenedNumber.toLocaleString("en-US", {
        maximumFractionDigits: 0
      });
    },
    surveyObject() {
      const survey = this.surveys.find(s => s.pk === this.surveyNum);
      if (survey) {
        return survey.survey_json.pages[0].elements;
      }
      return [];
    },
    size() {
      if (this.$vuetify.breakpoint.xs) {
        return 12;
      }
      return 6;
    },
    showCharts() {
      return this.currentObject.model === "survey" && this.surveyObject.length !== 0
    },
    showAggregateData() {
      return this.currentObject.model === "program" || this.currentObject.model === "project"
    },
    currentObject() {
      let object = {pk: -1, model: "", name: ""}
      if (this.selectedProgram !== null && this.isEmptyObject(this.selectedProgram)) {
        object = this.selectedProgram
        object.model = "program"
        if (this.selectedProject !== null && this.isEmptyObject(this.selectedProject)) {
          object = this.selectedProject
          object.model = "project"
          if (this.selectedSurvey !== null && this.isEmptyObject(this.selectedSurvey)) {
            object = this.selectedSurvey
            object.model = "survey"
          }
        }
      }
      return object
    },
    aggregateDatakey() {
      return `${this.currentObject.model} - ${this.currentObject.pk}`
    }
  },
  watch: {
    totalRespondents(newValue) {
      /* eslint-disable */
      TweenLite.to(this.$data, 1.5, { tweenedNumber: newValue }); // TweenLite is imported via CDN in index.html
      /* eslint-enable */
    }
  },
  beforeDestroy() {
    clearInterval(this.chartData);
  },
  created() {
    // Resets the selected objects whenever user navigates to different tabs
    this.updateSelectedProject({});
    this.updateSelectedProgram({});
    this.updateSelectedSurveyNumber(-1);

    // this looks redundant but otherwise you have to wait for the next timeout after selecting survey for the charts to update
    this.fetchData();
    this.getChartData();
    // The above two methods will be changed to be paginated so they don't error out when fetching takes > 5000ms (no syntax change needed, all in backend)

    this.fetchSurveys();
    this.getPrograms();
    this.getProjects();
  },
  methods: {
    ...mapActions({
      getPrograms: "programs/getPrograms",
      getProjects: "projects/getProjects",
      fetchSurveys: "survey/fetchSurveys",
      fetchData: "charts/fetchData",
      updateSelectedSurveyNumber: "survey/updateSelectedSurveyNumber",
      updateSelectedProgram: "programs/updateSelectedProgram",
      updateSelectedProject: "projects/updateSelectedProject",
    }),
    async print() {
      this.showLoading = true;
      const el = this.$refs.printMe;
      const options = {
        type: "dataURL",
        logging: false
      };
      const imgDatas = [];
      const divs = el.getElementsByClassName("col");
      for (let div of divs) {
        imgDatas.push(await this.$html2canvas(div, options));
      }
      // encode logo / image to Base64, which is what pdfJS requires - only needed when the image is not in the DOM/canvas already
      // NOTE: does not work with SVG, only tested with PNG / JPEG
      let logo = "data:image/jpeg;base64,";
      await image2base64("/static/SCOUT/Scout Default.png").then(encodedString => {
        logo += encodedString;
      });
      // the encoding of all the images is what takes so long - after the encoding is finished, remove the loading indicator
      Promise.all(imgDatas).then(() => {
        this.showLoading = false;
      });
      const pdf = new jsPDF({
        orientation: "p",
        unit: "in",
        format: [8, 11]
      });
      pdf.addImage(logo, "PNG", 1.5, 1); // 1.5 x-coord is an approximation for centering an image that is ~ 495px wide
      pdf.setFontSize(30);
      pdf.text("PROJECT TEXT LINE 1", 4, 4.5, null, null, "center");
      pdf.text("PROJECT TEXT LINE 2", 4, 5, null, null, "center");
      pdf.setFontSize(15);
      pdf.text("COUNTY NAME", 4, 5.5, null, null, "center");
      pdf.text("Date TBD", 4, 6, null, null, "center");
      pdf.text("Time TBD", 4, 6.5, null, null, "center");
      pdf.text("COORDINATOR NAME", 4, 7, null, null, "center");
      pdf.addPage();
      let x = 0.4;
      let y = 0.7;
      const imageWidth = 8;
      const imageHeight = 2.5869;
      imgDatas.forEach((img, index) => {
        // add the first chart to the top of the first page
        if (index === 0) {
          pdf.addImage(img, "JPG", x, y, imageWidth, imageHeight);
          return;
        }
        // after adding the first chart, add charts 2-per-page with a page in tbween
        if (index % 2 === 0) {
          pdf.addPage();
          pdf.addImage(img, "JPG", x, y, imageWidth, imageHeight);
        } else {
          pdf.addImage(img, "JPG", x, y + 5, imageWidth, imageHeight);
        }
      });
      pdf.save("charts_export.pdf");
    },
    getChartData() {
      this.chartData = setInterval(() => {
        this.$store.dispatch("charts/fetchData");
      }, 300000);
    },
    isPieChart(question) {
      return question.type === "radiogroup" && question.choices.length === 2;
    },
    isOneBarChart(question) {
      return question.type === "radiogroup" || question.type === "dropdown";
    },
    isBarChart(question) {
      return question.type === "checkbox" || question.type === "imagepicker";
    },
    isWordCloud(question) {
      return (
        (question.type === "comment" || question.type === "text") &&
        !this.$vuetify.breakpoint.xs
      );
    },
    reset() {
      this.$refs.programFilter.clearFilters();
      this.$refs.projectFilter.clearFilters();
      this.$refs.surveyFilter.clearFilters();
      this.updateSelectedSurveyNumber(-1);
    },
    isEmptyObject(object) {
      if (object !== null && typeof(object) === 'object') {
        return Object.keys(object).length !== 0
      }
    }
  }
};
</script>

<style scoped>
#filter-by-dropdown {
  padding: 2%;
  height: 100%;
}

@media (min-width: 320px) and (max-width: 1023px) {
  .export-button {
    top: 0;
  }
}

.dropdown-card {
  padding: 0%;
}

#charts-area {
  padding: 0.5%;
}

.filters {
  padding-left: 2%;
  padding-right: 1%;
}
</style>
